import * as RoutesModule from 'Routes';

import { createContext, useContext } from 'react';

import App from './App';
import { AppErrorBoundary } from 'components/ui/AppErrorBoundary';
import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';
import { AxiosInstance } from 'axios';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import { BrowserRouter } from 'react-router-dom';
import { ConfigurationService } from 'services/ConfigurationService';
import { InviteStore } from 'stores/InviteStore';
import { LanguageStore } from 'stores/LanguageStore';
import { NotificationsStore } from 'stores/NotificationsStore';
import { ProfileStore } from 'stores/ProfileStore';
import ReactDOM from 'react-dom/client';
import { ServiceTypes } from 'utilities/Constants';
import StartupService from 'services/StartupService';
import StartupStore from 'stores/StartupStore';
import registerServiceWorker from './registerServiceWorker';
import { ClientsStore } from './stores/ClientsStore';
import { PlayerPerformanceStore } from './stores/PlayerPerformanceStore';
import { SasTokenStore } from './stores/SasTokenStore';
import { UsersStore } from './stores/UsersStore';

const rootElement = document.getElementById('root');

type Stores = {
  errorHandler: AxiosErrorHandler;
  configurationService: ConfigurationService;
  authenticationService: AuthenticationService;
  apiServiceAxiosInstance: AxiosInstance;
  notificationStore: NotificationsStore;
  profileStore: ProfileStore;
  languageStore: LanguageStore;
  inviteStore: InviteStore;
  startupStore: StartupStore;
  usersStore: UsersStore;
  clientsStore: ClientsStore;
  playerPerformanceStore: PlayerPerformanceStore;
  sasTokenStore: SasTokenStore;
};

const StoresContext = createContext<Stores | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

async function RenderApp(root: ReturnType<typeof ReactDOM.createRoot>) {
  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const authenticationService = new AuthenticationService();

  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiServiceAxiosInstance = await serviceFactory.createInstance(ServiceTypes.apiServiceAxiosInstance);
  const notificationStore = new NotificationsStore();
  const profileStore = new ProfileStore(notificationStore);
  const languageStore = new LanguageStore(profileStore);
  const inviteStore = new InviteStore(notificationStore, profileStore);
  const usersStore = new UsersStore(profileStore, notificationStore);
  const clientsStore = new ClientsStore(notificationStore);
  const playerPerformanceStore = new PlayerPerformanceStore();
  const sasTokenStore = new SasTokenStore(notificationStore);

  const startupService = new StartupService(apiServiceAxiosInstance);
  const startupStore = new StartupStore(
    authenticationService,
    configurationService,
    profileStore,
    languageStore,
    startupService,
    notificationStore
  );

  const stores: Stores = {
    errorHandler,
    configurationService,
    authenticationService,
    apiServiceAxiosInstance,
    notificationStore,
    profileStore,
    languageStore,
    inviteStore,
    startupStore,
    usersStore,
    clientsStore,
    playerPerformanceStore,
    sasTokenStore,
  };

  AxiosServiceConfig.setDefaultDecorators(apiServiceAxiosInstance, authenticationService, errorHandler);

  root.render(
    <AppErrorBoundary>
      <BrowserRouter>
        <StoresContext.Provider value={stores}>
          <App>{RoutesModule.routes}</App>
        </StoresContext.Provider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

// Only bootstrap when a real DOM root exists (the browser). Under jsdom (tests)
// there is no #root, so modules can import this file without triggering createRoot.
if (rootElement) {
  registerServiceWorker();
  RenderApp(ReactDOM.createRoot(rootElement));
}
